import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import { Radio } from 'antd';
import clsx from 'clsx';
import fitContent from './fitContent';
import styles from './OptionGroup.module.css';

/**
 * @typedef {Object} OptionGroupOwnProps
 * @property {boolean} [fitContent] - Shrinks every option's content by one shared scale (down to
 *   `minScale`) so unbreakable content such as maths equations fits the row; below that floor
 *   the whole group scrolls horizontally as one, with each letter badge pinned on the left.
 * @property {number} [minScale] - Smallest scale `fitContent` may apply. Defaults to 0.8.
 */

/** @typedef {OptionGroupOwnProps & Omit<import('antd').RadioGroupProps, 'optionType' | 'buttonStyle'>} OptionGroupProps */

/**
 * The radiogroup counterpart to `Option` — a thin wrapper around antd's own `Radio.Group`
 * (`vertical` and `block` on by default), mirroring antd's `Radio`/`Radio.Group` split (`Group`
 * is attached onto `Option` the same way antd attaches it onto `Radio`). Because this *is* a real
 * `Radio.Group`, `value`/`onChange` behave exactly like antd's own — `onChange` fires with the
 * native change event off the selected `Option`'s underlying `<input type="radio">`, so
 * `event.target.value` resolves, which is what `Form.Item`'s default `getValueFromEvent` reads.
 * That makes this a genuine drop-in antd `Form.Item` control, not an approximation of one.
 *
 * @param {OptionGroupProps} props
 */
const OptionGroup = forwardRef(({ className, fitContent: fit, minScale, children, ...rest }, forwardedRef) => {
    const localRef = useRef(null);

    const setRef = useCallback(
        (node) => {
            localRef.current = node;
            if (typeof forwardedRef === 'function') forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
        },
        [forwardedRef],
    );

    useLayoutEffect(() => {
        if (!fit || !localRef.current) return undefined;
        return fitContent(localRef.current, { minScale });
    }, [fit, minScale, children]);

    return (
        <Radio.Group
            ref={setRef}
            vertical
            block
            className={clsx(styles.vecOptionGroup, fit && styles.vecOptionGroupFit, className)}
            {...rest}
        >
            {children}
        </Radio.Group>
    );
});
OptionGroup.displayName = 'OptionGroup';

export default OptionGroup;
